import {
  friendOf,
  goldFriend,
  goldLamp,
  lampGameOf,
  RUN_NAME,
  starsInWords,
  type RoadLamp,
  type RoadState,
} from '@goodtime/shared';

/**
 * What a tap on the gold pot, a lamp or the stars says, in plain words (the
 * panel's bubbles, PANEL_EXPLAINERS): what it is, and how it pays. On a road
 * the lamps are friends'; on a Wheel Run slot (`themeId`) the slot's stars
 * light them. Stars are words here: the panel draws them as pictures.
 */

/** The gold pot: closed until the friend with the slot's gold game joins
 *  (or, on a Wheel Run slot, its second star), then won in that game. */
export function explainGold(road: Pick<RoadState, 'lamps' | 'goldOpen'> | null, themeId?: string | null): string {
  const first = 'Gold pot. It grows a little with every spin.';
  const lamp = themeId ? goldLamp(themeId, road) : null;
  if (lamp) {
    // Only a run reaches it: a free first game doesn't play for it.
    return road?.goldOpen
      ? `${first} A ${RUN_NAME} that lands on its lamp opens ${lamp.title}, which can win the whole pot.`
      : `${first} The gold pot opens at ${starsInWords(lamp.stars)} on this slot. Then a ${RUN_NAME} that lands on its lamp can win it in ${lamp.title}.`;
  }
  const friend = goldFriend(road);
  if (!friend) return first;
  return road?.goldOpen
    ? `${first} A ${RUN_NAME} that lands on ${friend.name}’s lamp opens ${friend.title}, which can win the whole pot.`
    : `${first} It opens when ${friend.name} joins you. Then a ${RUN_NAME} that lands on ${friend.name}’s lamp can win it in ${friend.title}.`;
}

/** A friend's lamp, dark or lit; null for a prize lamp (the panel says
 *  what those pay). */
export function explainFriendLamp(lamp: RoadLamp): string | null {
  const friend = friendOf(lamp);
  if (!friend) return null;
  if (!lamp.lit) {
    return `${friend.name}’s lamp. It lights up when ${friend.name} joins you: restore their place on the road. ` +
      `Then a ${RUN_NAME} that lands here opens ${friend.title}, and what you win goes in your run pot.`;
  }
  return `${friend.title}. A ${RUN_NAME} that lands here opens ${friend.name}’s game, and what you win goes in your run pot.`;
}

/** A Wheel Run lamp on slot `themeId`, dark or lit; null for any other
 *  lamp. A dark one keeps its game a surprise till its star lights it. */
export function explainLampGame(lamp: RoadLamp, themeId: string): string | null {
  const game = lampGameOf(themeId, lamp);
  if (!game) return null;
  if (!lamp.lit) return `Reach ${starsInWords(lamp.needs ?? game.stars)} on this slot to light this lamp.`;
  return `${game.title}. A ${RUN_NAME} that lands here opens it, and what you win goes in your run pot.`;
}

/** How far the slot's stars have come (a Wheel Run slot), and what the
 *  next one brings: the next lamp, and the coins still to play. */
export function starsProgress(road: Pick<RoadState, 'stars' | 'wagered' | 'nextStarAt'>): { stars: number; toGo: number | null } {
  const stars = Math.max(0, Math.min(3, road.stars ?? 0));
  const toGo = stars >= 3 || road.nextStarAt == null ? null : Math.max(0, road.nextStarAt - (road.wagered ?? 0));
  return { stars, toGo };
}

/** The panel's line beside the stars: the coins until the next lamp, short
 *  when there's little room. Empty with every star. */
export function starsLine(road: Pick<RoadState, 'stars' | 'wagered' | 'nextStarAt'>, short = false): string {
  const { toGo } = starsProgress(road);
  if (toGo === null) return short ? 'Every lamp lit' : 'Every lamp is lit';
  const coins = toGo.toLocaleString('en-GB');
  return short ? `${coins} to go` : `Next lamp: ${coins} coins to go`;
}

/** A tap on the stars. */
export function explainStars(road: Pick<RoadState, 'stars' | 'wagered' | 'nextStarAt'>): string {
  const { stars, toGo } = starsProgress(road);
  const have = stars === 0 ? 'No stars on this slot yet.' : `You have ${starsInWords(stars)} on this slot.`;
  if (toGo === null) return `${have} Every lamp is lit, with a game on each.`;
  const next = starsInWords(stars + 1);
  const capital = next.charAt(0).toUpperCase() + next.slice(1);
  const verb = stars === 0 ? 'lights' : 'light';
  return `${have} Every spin here counts towards the next. ${capital} ${verb} the next lamp: ${toGo.toLocaleString('en-GB')} coins to go.`;
}
